import { FC, useState, useEffect, useRef } from 'react';
import {
  Theme,
  Button,
  ButtonStyles,
  FAIcon,
  Breakpoints,
  Notification,
  type NotificationTheme,
} from '@nextgen-web-framework/all';
import { ProfileInformationModeEnum, DynamicObjectInterface } from '@commerce-profile-webcomponents/sdk';
import { handleUIResize } from '../../utils/profileUtils';
import { getExt } from '../../utils/myAccountProfileUtils';
import { useMyAccount } from '../../utils/myAccountHook';
import { siteCountry } from '../../utils/helper';
import { MyAccountExtEnum } from '../../utils/types/enum';
import AccountInformationView from './AccountInformationView/AccountInformationView';
import AccountInformationEdit from './AccountInformationEdit/AccountInformationEdit';
import type { AccountInformationProps, ProfileButtonProps } from './types/propTypes';
import styles from './AccountInformation.module.scss';

const AccountInformation: FC<AccountInformationProps> = ({
  // master data
  masterDataConfig,
  sliceName,

  // account info
  profileId,
  accountNumber,

  // UI preference
  showFrame = true,
  removeBorder,
  screenTitle = '',
  editButtonText = '',
  showIcon = false,
}) => {
  const urlParams = new URLSearchParams(window.location.search);
  const showEdit = urlParams.get('showEdit'); // get confimation id

  const { t, breakpoint, isDesktop, isMobile, setConfig, accountCountryCode } = useMyAccount();
  const siteCountryCode = siteCountry()?.toLowerCase();

  const [saveDisabled, setSaveDisabled] = useState<boolean>(true);
  const [editingMode, setEditingMode] = useState<ProfileInformationModeEnum>(ProfileInformationModeEnum.VIEW);

  const saveButtonLabel = t('profile.ProfileInfo.buttons.save');
  const cancelButtonLabel = t('profile.ProfileInfo.buttons.cancel');

  const contentContainerRef = useRef<HTMLElement>(null);
  const profileEditFormRef = useRef<DynamicObjectInterface>(null);

  const iconSize = [Breakpoints.XS, Breakpoints.SM].includes(breakpoint as Breakpoints) ? 'fa-lg' : 'fa-xs';

  const { ext } = masterDataConfig;

  const editOnHomeCountryOnly = (getExt(MyAccountExtEnum.EDIT_ON_HOME_COUNTRY_ONLY, ext) as boolean) ?? false;
  const editableFlag = (getExt(MyAccountExtEnum.EDITABLE, ext) as boolean) ?? false;
  const editable = editOnHomeCountryOnly ? editableFlag && accountCountryCode === siteCountryCode : editableFlag;

  useEffect(() => {
    setConfig(masterDataConfig);
    // Initial check screen and scroll
    handleResize();

    // To detect screen scroll & resize
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);

    return () => {
      // To reset the event listener
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, []);

  useEffect(() => {
    if (showEdit === 'true') {
      setEditingMode(ProfileInformationModeEnum.EDIT);
    }
  }, [showEdit]);

  const primaryButton = (): ProfileButtonProps => {
    if (editingMode === ProfileInformationModeEnum.EDIT) {
      return {
        label: saveButtonLabel,
        visible: true,
        disabled: saveDisabled,
        onClick: () => profileEditFormRef?.current?.saveProfileHandler(),
      };
    } else {
      return {
        label: editButtonText,
        visible: true,
        onClick: () => setEditingMode(ProfileInformationModeEnum.EDIT),
      };
    }
  };

  const secondaryButton = (): ProfileButtonProps => {
    if (editingMode === ProfileInformationModeEnum.EDIT) {
      return {
        label: cancelButtonLabel,
        visible: true,
        onClick: () => setEditingMode(ProfileInformationModeEnum.VIEW),
      };
    } else {
      return {
        visible: false,
      };
    }
  };

  // UI functions
  const handleResize = () => {
    isDesktop &&
      handleUIResize({
        contentContainerRef,
        styles,
        buttonBarId: 'profile-button-bar',
      });
  };

  const handleButtonClick = (
    e: KeyboardEvent | MouseEvent | TouchEvent,
    onClick?: (e: DynamicObjectInterface) => void,
  ) => {
    handleResize();
    onClick?.(e);
  };

  const actionButtons = (primaryButton: ProfileButtonProps, secondaryButton?: ProfileButtonProps) => {
    const className = isDesktop ? styles['desktop-action-bar'] : styles['footer-action-bar'];

    return (
      <section className={className} data-testid="profile-button-bar" id="profile-button-bar">
        {secondaryButton?.visible && (
          <Button
            title={secondaryButton.label}
            onClick={(e: DynamicObjectInterface) => handleButtonClick(e, secondaryButton?.onClick)}
            disabled={secondaryButton.disabled}
            buttonStyle={ButtonStyles.OUTLINE}
            data-testid="profile-secondary-button"
          />
        )}
        {primaryButton?.visible && (
          <Button
            title={primaryButton.label}
            onClick={(e: DynamicObjectInterface) => handleButtonClick(e, primaryButton?.onClick)}
            disabled={primaryButton.disabled}
            buttonStyle={ButtonStyles.SOLID}
            data-testid="profile-primary-button"
          />
        )}
      </section>
    );
  };

  return (
    <div className={styles['account-info-container']} data-testid="account-info-container-testid">
      {showFrame && !isMobile && (
        <section className={styles['header']} data-testid="profile-header">
          <div className={styles['header-title']}>
            <div className={styles['text']} data-testid="profile-header-title">
              {screenTitle}
            </div>
            {showIcon && (
              <span className={styles['icon']}>
                <FAIcon
                  iconName={'circle-exclamation'}
                  className={`${styles['icon']} ${iconSize}`}
                  theme={Theme.ORANGE}
                  data-testid="profile-header-icon"
                />
              </span>
            )}
          </div>
        </section>
      )}
      <section className={styles['content']} data-testid="profile-content" ref={contentContainerRef}>
        {editingMode === ProfileInformationModeEnum.VIEW && (
          <>
            {editOnHomeCountryOnly && accountCountryCode !== siteCountryCode && (
              <div className={styles['edit-home-country']} data-testid="profile-edit-home-country">
                <Notification
                  theme={Theme.WARNING as NotificationTheme}
                  hasNotificationIcon
                  hasBoldText={false}
                  message={t('profile.ProfileInfo.notHomeCountryAlertMessage')}
                />
              </div>
            )}
            <AccountInformationView
              masterDataConfig={masterDataConfig}
              sliceName={sliceName}
              removeBorder={removeBorder}
            />
          </>
        )}
        {editingMode === ProfileInformationModeEnum.EDIT && (
          <AccountInformationEdit
            saveDisabledCallback={setSaveDisabled}
            setEditingMode={setEditingMode}
            masterDataConfig={masterDataConfig}
            sliceName={sliceName}
            ref={profileEditFormRef}
            profileId={profileId}
            accountNumber={String(accountNumber)}
          />
        )}
        {editable && isDesktop && actionButtons(primaryButton(), secondaryButton())}
      </section>
      {editable && !isDesktop && actionButtons(primaryButton(), secondaryButton())}
    </div>
  );
};

export default AccountInformation;
